// =============================================================================
// ActionButtons.tsx — Inline action buttons for AI messages (web port)
// =============================================================================

import React from 'react';
import { Box, Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import type { ActionButton } from '../../services/notesToHank/NotesToHankTypes';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ActionButtonsProps {
  actions: ActionButton[];
  onPress: (actionId: string, label: string, clientCommand?: string) => void;
  pressedIds: Set<string>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ActionButtons: React.FC<ActionButtonsProps> = ({
  actions,
  onPress,
  pressedIds,
}) => {
  const { theme } = useTheme();

  return (
    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {actions.map((action) => {
        const isPressed = pressedIds.has(action.id);
        return (
          <Button
            key={action.id}
            size="small"
            variant="contained"
            onClick={() =>
              onPress(action.id, action.label, action.clientCommand)
            }
            disabled={isPressed}
            startIcon={isPressed ? <CheckIcon fontSize="small" /> : undefined}
            aria-label={`${action.label}${isPressed ? ', done' : ''}`}
            sx={{
              borderRadius: '16px',
              textTransform: 'none',
              fontSize: 12,
              px: 1.5,
              py: 0.75,
              minWidth: 'auto',
              backgroundColor: theme.primary,
              color: theme.textCTA,
              opacity: isPressed ? 0.6 : 1,
              '&:hover': {
                backgroundColor: theme.primaryDark,
              },
              '&.Mui-disabled': {
                backgroundColor: theme.primary,
                color: theme.textCTA,
                opacity: 0.6,
              },
            }}
          >
            {action.label}
          </Button>
        );
      })}
    </Box>
  );
};

export default ActionButtons;
